import { createMapApp } from "./map-app.js";
import {
  createBridgeInputReceiptTracker,
  dispatchShellBridgeInputReceiptEvent,
  normalizeBridgeInputReceipt,
  setShellBridgeInputReceiptIngressReady,
  submitBridgeInputPatch,
} from "./map-bridge-input-receipts.js";
import { createEmptySnapshot } from "./map-bridge-contract.js";
import {
  characterBridgeInputPatchFromSignals,
  characterPresenceSnapshotForStreamAvailability,
  createCharacterPresenceBridgeController,
  createCharacterPresenceStreamHealthController,
} from "./map-character-presence-bridge.js";
import {
  createBrowserCharacterStreamAccessController,
} from "./map-character-stream-access.js";
import {
  applyV2StateRevisions,
  projectBookmarkDetailsChangedEvent,
  selectionSnapshotFromBridgeEventDetail,
} from "./map-bridge-output.js";
import FishyMapBridge from "./map-host.js";
import { sessionSnapshotToBridgePatch } from "./map-runtime-adapter.js";
import { browserMapSessionRepository } from "./map-session-repository.js";
import { createMapSessionLiveController } from "./map-session-live.js";
import { createMapPageDerivedController } from "./map-page-derived.js";
import { createMapPageLive } from "./map-page-live.js";
import { loadMapDatastarSignalAdapter } from "./map-datastar-signal-adapter.js";
import {
  publishMapShellProjectionV1,
} from "./map-shell-projection.js";
import { createMapShellDomainEventController } from "./map-shell-domain-event-controller.js";
import { createMapLootRateDisplayPreferenceController } from "./map-loot-rate-display-preference.js";
import { createMapPagePersistController } from "./map-page-persist.js";
import {
  bindFishydexCurrentMapProjection,
  bindMapPresetController,
} from "./map-presets.js";
import { languageReady, mapText } from "./map-i18n.js";
import {
  DEFAULT_MAP_ACTION_SIGNAL_STATE,
  DEFAULT_MAP_BOOKMARKS_SIGNAL_STATE,
  DEFAULT_MAP_BRIDGED_SIGNAL_STATE,
  DEFAULT_MAP_SESSION_SIGNAL_STATE,
  DEFAULT_MAP_UI_SIGNAL_STATE,
} from "./map-signal-contract.js";
import "./map-bookmark-panel-element.js";
import "./map-hover-tooltip-element.js";
import "./map-info-panel-element.js";
import "./map-layer-panel-element.js";
import "./map-query-input-element.js";
import "./map-tutorial-stage-element.js";
import "./map-window-manager-element.js";
import {
  combineSignalPatches,
} from "./map-signal-patch.js";
import { normalizeLootConditionContext } from "./map-loot-condition-context.js";
import {
  createCalculatorMapContextProjectionV1Owner,
} from "./map-calculator-context.js";
import { loadZoneCatalog } from "./map-zone-catalog.js";
import { dispatchShellZoneCatalogReadyEvent } from "./map-zone-catalog-live.js";
import { loadTradeNpcMapCatalog } from "./map-trade-summary.js";
import {
  dispatchShellTradeNpcCatalogChangedEvent,
} from "./map-trade-npc-catalog-live.js";
import {
  createMapCompassController,
  createMapZoomGaugeController,
} from "./map-view-controls.js";
import {
  createLiveEventsLayerGate,
} from "./map-live-events-layer.js";

let mapPageStartPromise = null;
let activeShellDomainEvents = null;
let activePagePersistor = null;
let activePageDerived = null;
let activeMapPresetController = null;
let activeCalculatorMapContext = null;
let activeLootRateDisplayPreference = null;
let activeMapPageLive = null;
let activeCharacterStreamAccess = null;
let activeLiveEventsLayerGate = null;

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function jsonObjectKeys(value) {
  return Object.keys(value).filter((key) => value[key] !== undefined);
}

function jsonValueEquals(left, right) {
  if (Object.is(left, right)) {
    return true;
  }
  const leftValue = left ?? null;
  const rightValue = right ?? null;
  if (Object.is(leftValue, rightValue)) {
    return true;
  }
  if (Array.isArray(leftValue) || Array.isArray(rightValue)) {
    if (!Array.isArray(leftValue) || !Array.isArray(rightValue)) {
      return false;
    }
    if (leftValue.length !== rightValue.length) {
      return false;
    }
    for (let index = 0; index < leftValue.length; index += 1) {
      if (!jsonValueEquals(leftValue[index], rightValue[index])) {
        return false;
      }
    }
    return true;
  }
  if (isPlainObject(leftValue) || isPlainObject(rightValue)) {
    if (!isPlainObject(leftValue) || !isPlainObject(rightValue)) {
      return false;
    }
    const leftKeys = jsonObjectKeys(leftValue);
    const rightKeys = jsonObjectKeys(rightValue);
    if (leftKeys.length !== rightKeys.length) {
      return false;
    }
    for (const key of leftKeys) {
      if (!Object.prototype.hasOwnProperty.call(rightValue, key)) {
        return false;
      }
      if (!jsonValueEquals(leftValue[key], rightValue[key])) {
        return false;
      }
    }
    return true;
  }
  return false;
}

function normalizeViewMode(value) {
  return value === "3d" ? "3d" : value === "2d" ? "2d" : null;
}

export function initialBridgeStateWithDesiredViewMode(initialState, desiredViewMode) {
  const viewMode = normalizeViewMode(desiredViewMode);
  if (!viewMode) {
    return initialState;
  }
  const next = cloneJson(initialState || {});
  next.commands = {
    ...(isPlainObject(next.commands) ? next.commands : {}),
    setViewMode: viewMode,
  };
  if (isPlainObject(next.commands.restoreView)) {
    next.commands.restoreView = {
      ...next.commands.restoreView,
      viewMode,
    };
  }
  return next;
}

export function normalizeCalculatorLootConditionContext(value) {
  return normalizeLootConditionContext(value);
}

function calculatorLootConditionContextPatch(context) {
  return {
    _map_bridged: {
      filters: {
        lootConditionContext: normalizeCalculatorLootConditionContext(context),
      },
    },
  };
}

export function resolveBridgeViewEventSnapshot(eventDetail, readCachedView) {
  const directCamera = isPlainObject(eventDetail?.camera) ? eventDetail.camera : null;
  const directViewMode = normalizeViewMode(eventDetail?.viewMode);
  if (!directCamera || !directViewMode) {
    throw new Error("Invalid FishyMap v2 view-changed event");
  }
  if (typeof readCachedView !== "function") {
    throw new Error("FishyMap view projection requires a cached view reader");
  }
  const currentSnapshot = readCachedView();
  if (!isPlainObject(currentSnapshot?.view)) {
    throw new Error("FishyMap cached view projection is invalid");
  }
  const baseView = cloneJson(currentSnapshot.view);
  return {
    view: {
      ...baseView,
      viewMode: directViewMode,
      camera: cloneJson(directCamera),
    },
  };
}

export function consumeMapSessionStartupBatchSuppression(armed) {
  return {
    captureSession: armed !== true,
    suppressNextBatch: false,
  };
}

export function runtimeSelectionSignalPatchFromBridgeEventDetail(detail) {
  const selection = selectionSnapshotFromBridgeEventDetail(detail);
  if (!selection) {
    return null;
  }
  return {
    _map_runtime: {
      selection: cloneJson(selection),
    },
  };
}

function runtimeReadySignalPatchFromBridgeEventDetail(detail) {
  if (detail?.type !== "ready") {
    return null;
  }
  return {
    _map_runtime: {
      ready: true,
    },
  };
}

export function patchTouchesLiveBridgeInputs(patch) {
  if (!isPlainObject(patch)) {
    return false;
  }
  return (
    "_map_bridged" in patch ||
    "_map_actions" in patch ||
    "_map_bookmarks" in patch ||
    "_shared_fish" in patch ||
    patch._map_ui?.windowUi?.settings?.autoAdjustView != null ||
    patch._map_ui?.windowUi?.settings?.sampleFromFilteredElements != null ||
    patch._map_ui?.windowUi?.settings?.threeDEnabled != null ||
    patch._map_ui?.windowUi?.settings?.effectsEnabled != null ||
    patch._map_ui?.windowUi?.settings?.focusWavesEnabled != null ||
    (isPlainObject(patch._map_ui?.search) &&
      Object.prototype.hasOwnProperty.call(patch._map_ui.search, "expression")) ||
    patch._map_ui?.layers?.hoverFactsVisibleByLayer != null
  );
}

export function resolveRuntimeCatalogRevision(stateRevisions, currentRevision = 0) {
  if (!Number.isSafeInteger(currentRevision) || currentRevision < 0) {
    throw new TypeError("FishyMap current catalog revision must be a non-negative safe integer");
  }
  if (stateRevisions == null) {
    return currentRevision;
  }
  return applyV2StateRevisions(
    { catalogRev: currentRevision, statusRev: 0 },
    stateRevisions,
  ).revisions.catalogRev;
}

export function runtimeStatusSignalPatchFromStateDelta(detail) {
  if (
    detail?.snapshotKind !== "bootstrap" ||
    typeof detail.ready !== "boolean" ||
    !isPlainObject(detail.statuses)
  ) {
    throw new TypeError("FishyMap bootstrap state delta requires ready and statuses");
  }
  return {
    _map_runtime: {
      ready: detail.ready,
      statuses: cloneJson(detail.statuses),
    },
  };
}

export function bookmarkDetailsSignalPatchFromBridgeEvent(signals, detail) {
  const projection = projectBookmarkDetailsChangedEvent(
    signals?._map_runtime?.ui?.bookmarks,
    signals?._map_bookmarks?.entries,
    detail,
  );
  if (!projection.applied) {
    return null;
  }
  return {
    _map_runtime: {
      ui: {
        bookmarks: projection.bookmarks,
      },
    },
  };
}

export function createBridgeInputPatchCoordinator({ patchBridgeFromSignals = () => {} } = {}) {
  let syncingFromBridge = false;
  let pendingBridgePatchAfterSync = false;
  let callbacksAfterSync = [];

  function requestBridgePatch() {
    if (syncingFromBridge) {
      pendingBridgePatchAfterSync = true;
      return false;
    }
    patchBridgeFromSignals();
    return true;
  }

  function runAfterSync(callback) {
    if (typeof callback !== "function") {
      return false;
    }
    if (syncingFromBridge) {
      callbacksAfterSync.push(callback);
      return true;
    }
    callback();
    return true;
  }

  function runBridgeSync(callback) {
    syncingFromBridge = true;
    try {
      return callback();
    } finally {
      syncingFromBridge = false;
      const shouldPatchBridge = pendingBridgePatchAfterSync;
      const callbacks = callbacksAfterSync;
      pendingBridgePatchAfterSync = false;
      callbacksAfterSync = [];
      if (shouldPatchBridge) {
        patchBridgeFromSignals();
      }
      for (const deferredCallback of callbacks) {
        deferredCallback();
      }
    }
  }

  return Object.freeze({
    isSyncingFromBridge: () => syncingFromBridge,
    requestBridgePatch,
    runAfterSync,
    runBridgeSync,
  });
}

export function buildResetUiPatch() {
  const mapUi = cloneJson(DEFAULT_MAP_UI_SIGNAL_STATE);
  if (isPlainObject(mapUi.windowUi?.settings)) {
    delete mapUi.windowUi.settings.lootRateDisplayMode;
  }
  return {
    _map_ui: mapUi,
    _map_bridged: cloneJson(DEFAULT_MAP_BRIDGED_SIGNAL_STATE),
    _map_bookmarks: cloneJson(DEFAULT_MAP_BOOKMARKS_SIGNAL_STATE),
    _map_session: cloneJson(DEFAULT_MAP_SESSION_SIGNAL_STATE),
    _map_actions: cloneJson(DEFAULT_MAP_ACTION_SIGNAL_STATE),
  };
}

const RUNTIME_STATUS_FIELDS = Object.freeze([
  ["metaStatus", "Meta"],
  ["layersStatus", "Layers"],
  ["zonesStatus", "Zones"],
  ["pointsStatus", "Points"],
  ["fishStatus", "Fish"],
  ["zoneStatsStatus", "Zone stats"],
]);

const API_STATUS_FIELDS = Object.freeze(["metaStatus", "zonesStatus", "pointsStatus", "fishStatus"]);
const API_FAILURE_STATUS_PATTERN =
  /\b(?:request failed|retrying|request closed|failed to fetch|connection refused|http\s+[45]\d{2})\b/i;
const API_STATUS_DETAIL_PATTERN = /\bapi="([^"]*)"/i;

function trimStatus(value) {
  return String(value ?? "").trim();
}

export function runtimeStatusLines(statuses) {
  if (!isPlainObject(statuses)) {
    return [];
  }
  return RUNTIME_STATUS_FIELDS.flatMap(([key, label]) => {
    const status = trimStatus(statuses[key]);
    return status ? [{ key, label, status }] : [];
  });
}

const MAP_LOADING_STAGE_TEXT_KEYS = Object.freeze({
  starting: "loading.starting",
  manifest: "loading.manifest",
  "module-import": "loading.module_import",
  "wasm-fetch": "loading.wasm_fetch",
  "wasm-compile": "loading.wasm_compile",
  "renderer-start": "loading.renderer_start",
  "runtime-bootstrap": "loading.runtime_bootstrap",
  "first-paint": "loading.first_paint",
  ready: "loading.ready",
  error: "error.renderer_unavailable",
});

const ACTIVE_RUNTIME_STATUS_PATTERN =
  /\b(?:pending|waiting|loading|retrying|request failed|request closed|failed to fetch|http\s+[45]\d{2})\b/i;

export function formatMapLoadingBytes(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) {
    return "";
  }
  const units = ["B", "KB", "MB", "GB"];
  let scaled = value;
  let unitIndex = 0;
  while (scaled >= 1024 && unitIndex < units.length - 1) {
    scaled /= 1024;
    unitIndex += 1;
  }
  const precision = unitIndex === 0 || scaled >= 10 ? 0 : 1;
  return `${scaled.toFixed(precision)} ${units[unitIndex]}`;
}

function loadingStageText(stage) {
  return mapText(MAP_LOADING_STAGE_TEXT_KEYS[stage] || MAP_LOADING_STAGE_TEXT_KEYS.starting);
}

function runtimeStatusLoadingDetail(statuses) {
  const statusLines = runtimeStatusLines(statuses);
  const activeLine =
    statusLines.find((line) => ACTIVE_RUNTIME_STATUS_PATTERN.test(line.status)) ||
    statusLines.find((line) => line.status);
  return activeLine ? `${activeLine.label}: ${activeLine.status}` : "";
}

function loadingDetailText(detail) {
  const loadedBytes = Number(detail?.loadedBytes);
  const totalBytes = Number(detail?.totalBytes);
  if (Number.isFinite(loadedBytes) && loadedBytes > 0) {
    const loaded = formatMapLoadingBytes(loadedBytes);
    const total = formatMapLoadingBytes(totalBytes);
    if (loaded && total) {
      return mapText("loading.fetch_bytes", { loaded, total });
    }
    if (loaded) {
      return mapText("loading.fetch_bytes_unknown", { loaded });
    }
  }
  return runtimeStatusLoadingDetail(detail?.statuses) || mapText("loading.initial_detail");
}

export function createMapLoadingOverlayController(shell) {
  const overlay = shell?.querySelector?.("#fishymap-loading-overlay") || null;
  if (!overlay) {
    return Object.freeze({
      update() {},
      updateFromSnapshot() {},
      finishAfterFirstPaint() {},
      fail() {},
    });
  }

  const stageElement = overlay.querySelector("#fishymap-loading-stage");
  const detailElement = overlay.querySelector("#fishymap-loading-detail");
  const progressElement = overlay.querySelector("#fishymap-loading-progress");
  const percentElement = overlay.querySelector("#fishymap-loading-percent");
  let hidden = false;
  let finished = false;
  let failed = false;
  let lastProgress = 0;

  overlay.addEventListener?.("transitionend", (event) => {
    if (hidden && event?.target === overlay && event?.propertyName === "opacity") {
      overlay.hidden = true;
    }
  });

  function setProgress(progress) {
    const normalized = Number(progress);
    if (!Number.isFinite(normalized)) {
      progressElement?.removeAttribute?.("value");
      if (percentElement) {
        percentElement.hidden = true;
        percentElement.textContent = "";
      }
      return;
    }
    lastProgress = Math.max(lastProgress, Math.min(1, Math.max(0, normalized)));
    const percent = Math.round(lastProgress * 100);
    progressElement?.setAttribute?.("value", String(percent));
    progressElement?.setAttribute?.("max", "100");
    if (percentElement) {
      percentElement.hidden = false;
      percentElement.textContent = `${percent}%`;
    }
  }

  function update(detail = {}) {
    if (hidden) {
      return;
    }
    shell.dataset.mapLoading = "loading";
    overlay.hidden = false;
    overlay.classList.remove("is-hidden");
    const stage = String(detail?.stage || "starting");
    overlay.dataset.stage = stage;
    if (stageElement) {
      stageElement.textContent = loadingStageText(stage);
    }
    if (detailElement) {
      detailElement.textContent = loadingDetailText(detail);
    }
    setProgress(detail?.progress);
  }

  function updateFromSnapshot(snapshot) {
    if (hidden || finished) {
      return;
    }
    if (snapshot?.ready === true) {
      finishAfterFirstPaint();
      return;
    }
    update({
      stage: "runtime-bootstrap",
      progress: 0.96,
      statuses: snapshot?.statuses,
    });
  }

  function hide() {
    if (failed) {
      return;
    }
    hidden = true;
    overlay.classList.add("is-hidden");
    shell.dataset.mapLoading = "hidden";
  }

  function finishAfterFirstPaint() {
    if (hidden || finished) {
      return;
    }
    finished = true;
    update({
      stage: "first-paint",
      progress: 0.98,
    });
    deferAfterAnimationFrames(
      () => {
        if (failed) {
          return;
        }
        update({
          stage: "ready",
          progress: 1,
        });
        deferAfterAnimationFrames(hide, { frames: 1 });
      },
      { frames: 2 },
    );
  }

  function fail(error) {
    failed = true;
    hidden = false;
    finished = true;
    shell.dataset.mapLoading = "error";
    overlay.hidden = false;
    overlay.classList.remove("is-hidden");
    overlay.dataset.stage = "error";
    progressElement?.classList?.remove("progress-primary");
    progressElement?.classList?.add("progress-error");
    progressElement?.removeAttribute?.("value");
    if (percentElement) {
      percentElement.hidden = true;
      percentElement.textContent = "";
    }
    if (stageElement) {
      stageElement.textContent = loadingStageText("error");
    }
    if (detailElement) {
      detailElement.textContent =
        error && typeof error === "object" && "message" in error
          ? String(error.message)
          : mapText("error.renderer_start_failed");
    }
  }

  update({
    stage: "starting",
    progress: null,
  });

  return Object.freeze({
    update,
    updateFromSnapshot,
    finishAfterFirstPaint,
    fail,
  });
}

export function apiFailureStatusLines(statuses) {
  if (!isPlainObject(statuses)) {
    return [];
  }
  return runtimeStatusLines(statuses).flatMap((line) => {
    if (!API_STATUS_FIELDS.includes(line.key)) {
      return [];
    }
    const attrMatch = line.status.match(API_STATUS_DETAIL_PATTERN);
    const rawStatus = trimStatus(attrMatch ? attrMatch[1] : line.status);
    const status = rawStatus.replace(/^[a-z][a-z0-9 _-]*:\s*/i, "");
    if (!API_FAILURE_STATUS_PATTERN.test(status)) {
      return [];
    }
    return [{ ...line, status }];
  });
}

function syncApiHealthIssues(apiFailures) {
  const health = globalThis.window?.__fishystuffPageHealth ?? globalThis.__fishystuffPageHealth;
  if (!health || typeof health.syncSourceIssues !== "function") {
    return;
  }
  health.syncSourceIssues("map-api", apiFailures.map((line) => ({
    id: `map-api:${line.key}`,
    severity: "warning",
    source: "map-api",
    category: "api",
    title: `${line.label} API request failed`,
    detail: line.status,
    context: {
      statusKey: line.key,
      status: line.status,
    },
  })));
}

export function renderRuntimeStatusSurface(root, statuses) {
  if (!root || typeof root.querySelector !== "function") {
    return;
  }

  const statusLines = runtimeStatusLines(statuses);
  const statusLinesElement = root.querySelector("#fishymap-status-lines");
  if (statusLinesElement) {
    const ownerDocument = root.ownerDocument || document;
    statusLinesElement.replaceChildren(
      ...statusLines.map((line) => {
        const row = ownerDocument.createElement("div");
        row.className = "flex min-w-0 items-start gap-2";

        const label = ownerDocument.createElement("span");
        label.className = "shrink-0 font-semibold text-base-content/55";
        label.textContent = `${line.label}:`;
        row.appendChild(label);

        const status = ownerDocument.createElement("span");
        status.className = "min-w-0 break-words";
        status.textContent = line.status;
        row.appendChild(status);

        return row;
      }),
    );
  }

  const apiFailures = apiFailureStatusLines(statuses);
  syncApiHealthIssues(apiFailures);
}

export function changedSignalPatch(projectedPatch, currentSignals) {
  if (!isPlainObject(projectedPatch)) {
    return null;
  }
  const nextPatch = {};
  for (const [rootKey, branch] of Object.entries(projectedPatch)) {
    if (!isPlainObject(branch)) {
      if (!Object.is(branch, currentSignals?.[rootKey])) {
        nextPatch[rootKey] = cloneJson(branch);
      }
      continue;
    }
    const currentBranch = isPlainObject(currentSignals?.[rootKey])
      ? currentSignals[rootKey]
      : {};
    const nextBranch = {};
    for (const [key, value] of Object.entries(branch)) {
      if (jsonValueEquals(value, currentBranch[key])) {
        continue;
      }
      nextBranch[key] = cloneJson(value);
    }
    if (Object.keys(nextBranch).length) {
      nextPatch[rootKey] = nextBranch;
    }
  }
  return Object.keys(nextPatch).length ? nextPatch : null;
}

function restoreViewPatchFromSessionView(view) {
  const patch = sessionSnapshotToBridgePatch({ view });
  return patch?.commands?.restoreView ? patch : null;
}

export function initialMapSessionRestorePatch(repositoryProjection) {
  const session = repositoryProjection?._map_session;
  if (!isPlainObject(session)) {
    return null;
  }
  const patch = sessionSnapshotToBridgePatch(session);
  return patch?.commands?.restoreView ? patch : null;
}

export function initialMapSessionPendingRestoreView(repositoryRestorePatch, finalInitialState) {
  if (!repositoryRestorePatch?.commands?.restoreView) {
    return null;
  }
  const restoreView = finalInitialState?.commands?.restoreView;
  return restoreView ? cloneJson(restoreView) : null;
}

export function restoreViewPatchFromSignalPatch(patch) {
  const view = patch?._map_session?.view;
  return isPlainObject(view) ? restoreViewPatchFromSessionView(view) : null;
}

export function bridgeSnapshotMatchesRestoreView(snapshot, restoreView) {
  if (!restoreView) {
    return true;
  }
  const snapshotRestoreView = restoreViewPatchFromSessionView(snapshot?.view)?.commands?.restoreView;
  if (!snapshotRestoreView) {
    return false;
  }
  if (jsonValueEquals(snapshotRestoreView, restoreView)) {
    return true;
  }
  if (snapshotRestoreView.viewMode !== restoreView.viewMode) {
    return false;
  }
  const snapshotCamera = isPlainObject(snapshotRestoreView.camera) ? snapshotRestoreView.camera : {};
  const restoreCamera = isPlainObject(restoreView.camera) ? restoreView.camera : {};
  const stableCameraKeys = Object.keys(restoreCamera).filter((key) => key !== "zoom" && key !== "distance");
  return stableCameraKeys.length > 0
    && stableCameraKeys.every((key) => Object.is(snapshotCamera[key], restoreCamera[key]));
}

export function deferAfterAnimationFrames(
  callback,
  {
    frames = 1,
    requestAnimationFrameImpl = globalThis.requestAnimationFrame?.bind(globalThis),
  } = {},
) {
  const remainingFrames = Math.max(0, Number.parseInt(frames, 10) || 0);
  const invoke = () => {
    if (typeof callback === "function") {
      callback();
    }
  };

  const step = (remaining) => {
    if (remaining <= 0) {
      invoke();
      return;
    }
    const next = () => step(remaining - 1);
    if (typeof requestAnimationFrameImpl === "function") {
      requestAnimationFrameImpl(next);
      return;
    }
    next();
  };

  step(remainingFrames);
}

async function startMapPageRuntime() {
  const shell = document.getElementById("map-page-shell");
  const canvas = document.getElementById("bevy");
  if (!(shell instanceof HTMLElement) || !(canvas instanceof HTMLCanvasElement)) {
    return;
  }
  setShellBridgeInputReceiptIngressReady(shell, false);
  const app = createMapApp();
  const bridge = FishyMapBridge;
  const mapSessionRepository = browserMapSessionRepository();
  const mapSessionLive = createMapSessionLiveController({
    repository: mapSessionRepository,
    eventTarget: globalThis.window,
    readCurrentView: () => currentBridgeViewSnapshot().view,
  });
  let characterPresenceBridge = null;
  let characterStreamHealth = null;
  let tradeNpcMapCatalog = null;
  const characterStreamAvailable = () => characterStreamHealth?.isAvailable() === true;
  const latestCharacterPresence = () => characterStreamHealth?.latestPresenceSnapshot() ?? null;
  const loadingOverlay = createMapLoadingOverlayController(shell);
  void bridge.prepareRuntime({
    onLoadProgress: (detail) => loadingOverlay.update(detail),
  });
  let lastBridgeViewSnapshot = createEmptySnapshot().view;
  const zoomGauge = createMapZoomGaugeController(shell, {
    readSnapshot: currentBridgeViewSnapshot,
    dispatchCommand: (command) => bridge.sendCommand(command),
  });
  const compass = createMapCompassController(shell, {
    readSnapshot: currentBridgeViewSnapshot,
    dispatchCommand: (command) => bridge.sendCommand(command),
  });

  const page = createMapPageLive({
    signalAdapter: await loadMapDatastarSignalAdapter(),
  });
  activeLootRateDisplayPreference?.stop();
  activeLootRateDisplayPreference = null;
  activeMapPageLive?.stop();
  activeMapPageLive = page;
  const bridgeInputReceipts = createBridgeInputReceiptTracker({
    publish(eventType, detail) {
      dispatchShellBridgeInputReceiptEvent(shell, eventType, detail);
    },
  });
  if (!page.start()) {
    throw new Error("FishyMap page signal lifecycle failed to start");
  }
  await page.whenRestored();
  const initialMapSessionPatch = mapSessionLive.initialSignalPatch();
  if (initialMapSessionPatch) {
    page.patchSignals(initialMapSessionPatch);
  }
  const mapShellProjection = page;
  mapShellProjection.subscribe((projection) => {
    publishMapShellProjectionV1(shell, projection);
  });
  activeCharacterStreamAccess?.stop();
  activeCharacterStreamAccess = null;
  activeLiveEventsLayerGate?.stop();
  const liveEventsLayerGate = createLiveEventsLayerGate({
    readProjection: () => mapShellProjection.current(),
    subscribeProjection: (subscriber) => mapShellProjection.subscribe(subscriber),
    applyPatch: dispatchSignalPatch,
    onEligibilityChange(eligible) {
      const layerPanel = shell.querySelector?.("fishymap-layer-panel");
      if (layerPanel) {
        layerPanel.liveEventsEligible = eligible;
      }
    },
  });
  activeLiveEventsLayerGate = liveEventsLayerGate;
  liveEventsLayerGate.start();
  const characterStreamAccess = createBrowserCharacterStreamAccessController({
    shell,
    onAccessChange(access) {
      liveEventsLayerGate.setAccess(access);
    },
    onStreamMounted(streamElement) {
      characterStreamHealth?.dispose();
      characterStreamHealth = createCharacterPresenceStreamHealthController({
        streamElement,
        onAvailabilityChange(available) {
          characterPresenceBridge?.setStreamAvailable(available);
          liveEventsLayerGate.setStreamAvailable(available);
        },
        onPresenceSnapshot(presence) {
          characterPresenceBridge?.submitPresence(presence);
          liveEventsLayerGate.setPresence(presence);
        },
      });
      characterStreamHealth.start();
    },
    onStreamUnmounted() {
      characterStreamHealth?.dispose();
      characterStreamHealth = null;
      characterPresenceBridge?.setStreamAvailable(false);
      liveEventsLayerGate.setStreamAvailable(false);
      liveEventsLayerGate.setPresence(null);
    },
  });
  activeCharacterStreamAccess = characterStreamAccess;
  characterStreamAccess.start();
  let mounted = false;
  const bridgeInputPatchCoordinator = createBridgeInputPatchCoordinator({
    patchBridgeFromSignals,
  });
  const shellDomainEvents = createMapShellDomainEventController({
    shell,
    readProjection: () => mapShellProjection.current(),
    dispatchPatch: dispatchSignalPatch,
    requestBridgeInput: () => bridgeInputPatchCoordinator.requestBridgePatch(),
    readTradeNpcCatalog: () => tradeNpcMapCatalog,
    prepareCharacterActivityRefresh() {
      const stream = shell.querySelector?.("[data-map-character-activity-stream]") || null;
      if (!stream || typeof globalThis.CustomEvent !== "function") {
        return null;
      }
      return () => {
        stream.dispatchEvent(
          new globalThis.CustomEvent("fishymap-character-activity-change"),
        );
      };
    },
  });
  activeShellDomainEvents?.stop();
  activeShellDomainEvents = shellDomainEvents;
  shellDomainEvents.start();
  mapSessionLive.start();
  shell.querySelector("fishymap-window-manager")?.applyFromSignals?.();
  const pagePersistor = createMapPagePersistController({
    globalRef: globalThis,
    isReady: () => true,
    readProjection: () => mapShellProjection.current(),
    subscribeProjection: (subscriber, options) => mapShellProjection.subscribe(
      subscriber,
      options,
    ),
  });
  activePagePersistor?.stop();
  activePagePersistor = pagePersistor;
  if (!pagePersistor.start()) {
    activePagePersistor = null;
    throw new Error("FishyMap page persistence failed to start");
  }
  const lootRateDisplayPreference = createMapLootRateDisplayPreferenceController({
    root: shell.querySelector("#fishymap-loot-rate-display-mode"),
    readProjection: () => mapShellProjection.current(),
    dispatchPatch: dispatchSignalPatch,
  });
  lootRateDisplayPreference.start();
  activeLootRateDisplayPreference = lootRateDisplayPreference;

  function dispatchSignalPatch(patch, receipt = null) {
    if (!patch || typeof patch !== "object") {
      return false;
    }
    const normalizedReceipt = receipt == null
      ? null
      : normalizeBridgeInputReceipt(receipt);
    if (normalizedReceipt && !patchTouchesLiveBridgeInputs(patch)) {
      const error = new TypeError(
        "FishyMap tracked signal patch must change a live bridge input",
      );
      bridgeInputReceipts.fail(normalizedReceipt, error, { stage: "signal-patch" });
      throw error;
    }
    try {
      page.patchSignals(patch, {
        beforeNotify() {
          if (normalizedReceipt) {
            bridgeInputReceipts.queue(normalizedReceipt);
          }
        },
      });
    } catch (error) {
      if (normalizedReceipt) {
        bridgeInputReceipts.fail(normalizedReceipt, error, { stage: "signal-patch" });
      }
      throw error;
    }
    return true;
  }
  function applyCalculatorMapContextProjection(projection) {
    shell.calculatorMapContextProjection = projection;
    const infoPanel = shell.querySelector?.("fishymap-info-panel") || null;
    if (infoPanel) {
      infoPanel.calculatorMapContextProjection = projection;
    }
    const context = projection.calculator.state === "ready"
      ? {
          fishingMastery: projection.calculator.fishingMastery,
          fishingLifeskillLevel: projection.calculator.fishingLifeskillLevel,
        }
      : {};
    const changedPatch = changedSignalPatch(
      calculatorLootConditionContextPatch(context),
      signals(),
    );
    if (changedPatch) {
      dispatchSignalPatch(changedPatch);
    }
  }
  activeCalculatorMapContext?.dispose();
  activeCalculatorMapContext = null;
  const calculatorMapContext = createCalculatorMapContextProjectionV1Owner({
    onProjection: applyCalculatorMapContextProjection,
  });
  activeCalculatorMapContext = calculatorMapContext;
  const fishydexCurrentProjection = bindFishydexCurrentMapProjection({
    readFishCatalog() {
      const fish = currentBridgeState()?.catalog?.fish;
      return Array.isArray(fish) ? fish : [];
    },
    sharedFishState: globalThis.window?.__fishystuffSharedFishState,
    applyPatch(patch) {
      const changedPatch = changedSignalPatch(patch, signals());
      if (changedPatch) {
        dispatchSignalPatch(changedPatch);
      }
    },
  });
  activeMapPresetController?.dispose();
  activeMapPresetController = null;
  const mapPresetController = bindMapPresetController({
    readProjection: () => mapShellProjection.current(),
    subscribeProjection: (subscriber, options) => mapShellProjection.subscribe(
      subscriber,
      options,
    ),
    applyPatch: dispatchSignalPatch,
    readBridgeState: currentBridgeState,
  });
  activeMapPresetController = mapPresetController;
  await Promise.all([
    calculatorMapContext.ready,
    fishydexCurrentProjection.ready,
    mapPresetController.ready,
  ]);
  const pageDerived = createMapPageDerivedController({
    globalRef: globalThis,
    readProjection: () => mapShellProjection.current(),
    subscribeProjection: (subscriber, options) => mapShellProjection.subscribe(
      subscriber,
      options,
    ),
    dispatchPatch: dispatchSignalPatch,
  });
  pageDerived.applyInitialPatches();
  activePageDerived?.stop();
  activePageDerived = null;
  if (!pageDerived.start()) {
    throw new Error("FishyMap page-derived projection failed to start");
  }
  activePageDerived = pageDerived;

  const nowMs = () => {
    const performanceNow = globalThis.performance?.now?.();
    if (Number.isFinite(performanceNow)) {
      return performanceNow;
    }
    return Date.now();
  };
  const runtimeStartedAtMs = nowMs();
  let applyingInternalSignalPatch = false;
  let lastBridgePatch = null;
  let pendingBridgeRestoreView = null;
  let suppressNextSessionCaptureBatch = false;
  let actionState = app.readLastActionState();
  let lastBridgeStateRevisions = { catalogRev: 0, statusRev: 0 };
  let lastFishydexCatalogRevision = -1;
  const recordRuntimeReady = () => {
    globalThis.__fishystuffTelemetry?.recordDurationVital?.(
      "map_runtime_ready",
      Math.max(0, nowMs() - runtimeStartedAtMs),
    );
    loadingOverlay.finishAfterFirstPaint();
  };

  function signals() {
    return page.currentSignals();
  }

  function currentBridgeState() {
    return bridge.getCurrentState();
  }

  function rememberBridgeViewSnapshot(snapshot) {
    if (isPlainObject(snapshot?.view)) {
      lastBridgeViewSnapshot = cloneJson(snapshot.view);
    }
  }

  function currentBridgeViewSnapshot() {
    return {
      view: cloneJson(lastBridgeViewSnapshot),
    };
  }

  function updateViewControlsFromSnapshot(snapshot) {
    rememberBridgeViewSnapshot(snapshot);
    zoomGauge.update(snapshot);
    compass.update(snapshot);
  }

  function publishCachedBridgeState(stateRevisions = null) {
    const snapshot = currentBridgeState();
    patchSignalsFromBridge(snapshot, stateRevisions);
    loadingOverlay.updateFromSnapshot(snapshot);
    updateViewControlsFromSnapshot(snapshot);
    if (snapshot?.ready === true) {
      recordRuntimeReady();
    }
    renderRuntimeStatusSurface(shell, snapshot?.statuses);
  }


  function nextBridgePatch() {
    const currentSignals = signals();
    return app.nextBridgePatch(currentSignals, {
      contestSelection: shell.querySelector("#fishymap-query-contest")?.selectionProjection,
      titleProjection: shell.querySelector("fishymap-query-input")?.titleQueryProjection,
    });
  }

  function patchBridgeFromSignals() {
    if (!mounted || bridgeInputPatchCoordinator.isSyncingFromBridge()) {
      return;
    }
    const patch = nextBridgePatch();
    if (jsonValueEquals(patch, lastBridgePatch) && !patch?.commands) {
      if (bridgeInputReceipts.hasQueued()) {
        const error = new TypeError(
          "FishyMap tracked signal patch did not change the aggregate bridge input",
        );
        bridgeInputReceipts.failQueued(error, { stage: "bridge-submit" });
        throw error;
      }
      return;
    }
    const inputSequence = submitBridgeInputPatch(bridge, patch, bridgeInputReceipts);
    lastBridgePatch = patch;
    try {
      actionState = app.consumeSignals(signals());
    } finally {
      bridgeInputReceipts.submitted(inputSequence);
    }
  }

  function applyInternalSignalPatch(patch) {
    if (applyingInternalSignalPatch) {
      return;
    }
    applyingInternalSignalPatch = true;
    try {
      dispatchSignalPatch(patch);
    } finally {
      applyingInternalSignalPatch = false;
    }
  }

  function patchSignalsFromBridge(snapshot, stateRevisions = null) {
    bridgeInputPatchCoordinator.runBridgeSync(() => {
      const currentSignals = signals();
      const sessionPatch = pendingBridgeRestoreView
        ? (
            bridgeSnapshotMatchesRestoreView(snapshot, pendingBridgeRestoreView)
              ? app.projectSessionSnapshot(snapshot)
              : null
          )
        : app.projectSessionSnapshot(snapshot);
      if (pendingBridgeRestoreView && sessionPatch) {
        pendingBridgeRestoreView = null;
      }
      const catalogRevision = resolveRuntimeCatalogRevision(
        stateRevisions,
        currentSignals?._map_runtime?.catalogRevision,
      );
      const projectedPatch = combineSignalPatches(
        app.projectRuntimeSnapshot(snapshot, { catalogRevision }),
        sessionPatch,
      );
      const changedPatch = changedSignalPatch(projectedPatch, currentSignals);
      if (changedPatch) {
        dispatchSignalPatch(changedPatch);
      }
      if (catalogRevision !== lastFishydexCatalogRevision) {
        lastFishydexCatalogRevision = catalogRevision;
        fishydexCurrentProjection.refresh();
      }
    });
  }

  function restoreBridgeViewFromRestorePatch(restorePatch) {
    const restoreView = restorePatch?.commands?.restoreView;
    if (!restoreView) {
      return false;
    }
    pendingBridgeRestoreView = cloneJson(restoreView);
    bridge.setState(restorePatch);
    return true;
  }

  function handleBridgeStateEvent(event, { captureSession = true } = {}) {
    if (event?.type === "view-changed") {
      const snapshot = resolveBridgeViewEventSnapshot(event?.detail, currentBridgeViewSnapshot);
      updateViewControlsFromSnapshot(snapshot);
      if (captureSession) {
        mapSessionLive.recordViewSnapshot(snapshot);
      }
      if (
        pendingBridgeRestoreView
        && bridgeSnapshotMatchesRestoreView(snapshot, pendingBridgeRestoreView)
      ) {
        pendingBridgeRestoreView = null;
      }
      return;
    }
    if (event?.type === "hover-changed") {
      return;
    }
    if (event?.type === "hotspot-visibility-changed") {
      return;
    }
    if (event?.type === "selection-changed") {
      const selectionPatch = runtimeSelectionSignalPatchFromBridgeEventDetail(event?.detail);
      const changedPatch = changedSignalPatch(
        selectionPatch,
        signals(),
      );
      if (changedPatch) {
        dispatchSignalPatch(changedPatch);
      }
      if (captureSession) {
        mapSessionLive.recordSelectionSnapshot(selectionPatch?._map_runtime?.selection);
      }
      return;
    }
    if (event?.type === "bookmark-details-changed") {
      const changedPatch = changedSignalPatch(
        bookmarkDetailsSignalPatchFromBridgeEvent(signals(), event?.detail),
        signals(),
      );
      if (changedPatch) {
        dispatchSignalPatch(changedPatch);
      }
      return;
    }
    if (event?.type === "ready") {
      const changedPatch = changedSignalPatch(
        runtimeReadySignalPatchFromBridgeEventDetail(event?.detail),
        signals(),
      );
      if (changedPatch) {
        dispatchSignalPatch(changedPatch);
      }
      recordRuntimeReady();
      return;
    }
    if (event?.type === "first-frame") {
      recordRuntimeReady();
      return;
    }
    if (event?.type === "snapshot-ready" && isPlainObject(event?.detail?.snapshot)) {
      const snapshot = event.detail.snapshot;
      patchSignalsFromBridge(snapshot, event?.stateRevisions);
      loadingOverlay.updateFromSnapshot(snapshot);
      updateViewControlsFromSnapshot(snapshot);
      if (snapshot?.ready === true) {
        recordRuntimeReady();
      }
      renderRuntimeStatusSurface(shell, snapshot?.statuses);
      return;
    }
    if (event?.type === "state-delta") {
      if (event?.detail?.snapshotKind === "bootstrap") {
        const statusSnapshot = {
          ready: event.detail.ready,
          statuses: event.detail.statuses,
        };
        const changedPatch = changedSignalPatch(
          runtimeStatusSignalPatchFromStateDelta(event.detail),
          signals(),
        );
        if (changedPatch) {
          dispatchSignalPatch(changedPatch);
        }
        loadingOverlay.updateFromSnapshot(statusSnapshot);
        if (statusSnapshot.ready) {
          recordRuntimeReady();
        }
        renderRuntimeStatusSurface(shell, statusSnapshot.statuses);
        return;
      }
      if (event?.detail?.snapshotKind !== "state") {
        throw new TypeError("FishyMap state delta requires an exact snapshot kind");
      }
      publishCachedBridgeState(event?.stateRevisions);
      return;
    }
    throw new Error(`Unsupported or invalid FishyMap v2 page event: ${String(event?.type || "")}`);
  }

  shell.addEventListener("fishymap:v2", (event) => {
    if (!Array.isArray(event?.detail?.events)) {
      throw new Error("Invalid FishyMap v2 page event batch");
    }
    const revisionUpdate = applyV2StateRevisions(
      lastBridgeStateRevisions,
      event.detail.stateRevisions,
    );
    lastBridgeStateRevisions = revisionUpdate.revisions;
    const events = event.detail.events;
    const capture = consumeMapSessionStartupBatchSuppression(
      suppressNextSessionCaptureBatch,
    );
    suppressNextSessionCaptureBatch = capture.suppressNextBatch;
    for (const entry of events) {
      handleBridgeStateEvent({
        type: entry?.type,
        detail: entry,
        stateRevisions: lastBridgeStateRevisions,
      }, { captureSession: capture.captureSession });
    }
    const appliedInputSequence = event.detail.appliedInputSequence;
    if (Number.isSafeInteger(appliedInputSequence) && appliedInputSequence >= 0) {
      bridgeInputReceipts.acknowledge(appliedInputSequence);
    }
  });
  function handleSignalPatch(patch) {
    if (applyingInternalSignalPatch) {
      return;
    }
    characterPresenceBridge?.submitSignalPatch(patch);
    const restorePatch = restoreViewPatchFromSignalPatch(patch);
    const restoredBridgeView = restorePatch?.commands?.restoreView
      ? bridgeInputPatchCoordinator.runAfterSync(() => {
          restoreBridgeViewFromRestorePatch(restorePatch);
        })
      : false;
    const touchesLiveBridgeInputs = patchTouchesLiveBridgeInputs(patch);
    if (!restoredBridgeView && !touchesLiveBridgeInputs) {
      return;
    }

    const nextActionState = {
      ...(signals()?._map_actions || {}),
      ...(patch?._map_actions || {}),
    };
    const resetUiToken = Number(nextActionState.resetUiToken || 0);
    const previousResetUiToken = Number(actionState.resetUiToken || 0);
    if (resetUiToken > previousResetUiToken) {
      applyInternalSignalPatch(buildResetUiPatch());
    }

    if (touchesLiveBridgeInputs) {
      bridgeInputPatchCoordinator.requestBridgePatch();
    }
  }
  page.subscribePatches(handleSignalPatch);

  const initialPatch = nextBridgePatch();
  const initialCharacterPatch = characterBridgeInputPatchFromSignals(signals());
  initialCharacterPatch.characterPresence = characterPresenceSnapshotForStreamAvailability(
    latestCharacterPresence() || initialCharacterPatch.characterPresence,
    characterStreamAvailable(),
  );
  const initialRestorePatch = initialMapSessionRestorePatch(initialMapSessionPatch);
  await languageReady();
  const mergedInitialState = {
    ...cloneJson(initialPatch),
    ...cloneJson(initialCharacterPatch),
    ...(initialRestorePatch || {}),
    ...(initialPatch.filters || initialRestorePatch?.filters
      ? {
          filters: {
            ...(isPlainObject(initialRestorePatch?.filters)
              ? cloneJson(initialRestorePatch.filters)
              : {}),
            ...(isPlainObject(initialPatch.filters) ? cloneJson(initialPatch.filters) : {}),
          },
        }
      : {}),
    ...(initialPatch.ui || initialRestorePatch?.ui
      ? {
          ui: {
            ...(isPlainObject(initialRestorePatch?.ui) ? cloneJson(initialRestorePatch.ui) : {}),
            ...(isPlainObject(initialPatch.ui) ? cloneJson(initialPatch.ui) : {}),
          },
        }
      : {}),
    ...(initialPatch.commands || initialRestorePatch?.commands
      ? {
          commands: {
            ...(isPlainObject(initialRestorePatch?.commands)
              ? cloneJson(initialRestorePatch.commands)
              : {}),
            ...(isPlainObject(initialPatch.commands) ? cloneJson(initialPatch.commands) : {}),
          },
        }
      : {}),
  };
  const initialBridgeState = initialBridgeStateWithDesiredViewMode(
    mergedInitialState,
    initialPatch?.ui?.viewMode,
  );
  pendingBridgeRestoreView = initialMapSessionPendingRestoreView(
    initialRestorePatch,
    initialBridgeState,
  );
  suppressNextSessionCaptureBatch = pendingBridgeRestoreView !== null;
  await bridge.mount(shell, {
    canvas,
    onLoadProgress: (detail) => loadingOverlay.update(detail),
    initialState: initialBridgeState,
  });
  mounted = true;
  characterPresenceBridge = createCharacterPresenceBridgeController({
    bridge,
    readSignals: signals,
    streamAvailable: characterStreamAvailable(),
  });
  const currentCharacterState = characterBridgeInputPatchFromSignals(signals());
  characterPresenceBridge.submitCatalog(currentCharacterState.characterCatalog);
  characterPresenceBridge.submitPresence(
    latestCharacterPresence() || currentCharacterState.characterPresence,
  );
  actionState = app.consumeSignals(signals());
  lastBridgePatch = initialPatch;
  setShellBridgeInputReceiptIngressReady(shell, true);
  const postMountSnapshot = currentBridgeState();
  patchSignalsFromBridge(postMountSnapshot);
  loadingOverlay.updateFromSnapshot(postMountSnapshot);
  updateViewControlsFromSnapshot(postMountSnapshot);
  renderRuntimeStatusSurface(
    shell,
    postMountSnapshot?.statuses || signals()?._map_runtime?.statuses,
  );
  if (postMountSnapshot?.ready === true) {
    recordRuntimeReady();
  }
  patchBridgeFromSignals();
  void loadZoneCatalog().then((loadedZoneCatalog) => {
    dispatchShellZoneCatalogReadyEvent(
      shell,
      Array.isArray(loadedZoneCatalog) ? cloneJson(loadedZoneCatalog) : [],
    );
  });
  dispatchShellTradeNpcCatalogChangedEvent(shell, { status: "loading" });
  void loadTradeNpcMapCatalog()
    .then((catalog) => {
      tradeNpcMapCatalog = catalog;
      dispatchShellTradeNpcCatalogChangedEvent(shell, {
        catalog,
        status: "loaded",
      });
    })
    .catch(() => {
      dispatchShellTradeNpcCatalogChangedEvent(shell, { status: "error" });
    });
}

export async function start() {
  if (!mapPageStartPromise) {
    mapPageStartPromise = startMapPageRuntime().catch((error) => {
      activeCharacterStreamAccess?.stop();
      activeCharacterStreamAccess = null;
      activeLiveEventsLayerGate?.stop();
      activeLiveEventsLayerGate = null;
      activeCalculatorMapContext?.dispose();
      activeCalculatorMapContext = null;
      activeMapPresetController?.dispose();
      activeMapPresetController = null;
      activePageDerived?.stop();
      activePageDerived = null;
      activePagePersistor?.stop();
      activePagePersistor = null;
      activeShellDomainEvents?.stop();
      activeShellDomainEvents = null;
      activeLootRateDisplayPreference?.stop();
      activeLootRateDisplayPreference = null;
      activeMapPageLive?.stop();
      activeMapPageLive = null;
      mapPageStartPromise = null;
      throw error;
    });
  }
  return mapPageStartPromise;
}

export function startWhenDomReady() {
  const run = () => {
    start().catch((error) => {
      console.error("Fishy map app bootstrap failed", error);
      createMapLoadingOverlayController(document.getElementById("map-page-shell"))?.fail?.(error);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run, { once: true });
    return;
  }

  run();
}
